import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	ChevronDown,
	Database,
	Mail,
	MessageSquare,
	PenLine,
	Phone,
	UserPlus,
	UsersRound,
	X,
} from "lucide-react";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useRole } from "@/hooks/use-role";
import { useSession } from "@/hooks/use-session";
import { apiService } from "@/lib/api-service";
import { trackVoterDetailViewed } from "@/lib/analytics-events";
import type { VoterFileContactDetail } from "@/types/voter-file";

export const Route = createFileRoute("/_app/_/contacts/voter-file/$l2VoterId")({
	component: VoterFileContactDetailPage,
});

function formatScope(scope: Record<string, string>): string {
	const parts: string[] = [];
	if (scope.state) parts.push(scope.state);
	if (scope.congressional_district)
		parts.push(`CD-${scope.congressional_district}`);
	if (scope.county) parts.push(scope.county);
	if (scope.city) parts.push(scope.city);
	return parts.join(" • ");
}

function fmt(
	value: string | number | boolean | null | undefined,
	fallback = "\u2014",
): string {
	if (value === null || value === undefined || value === "") return fallback;
	if (typeof value === "boolean") return value ? "Yes" : "No";
	return String(value);
}

function fullAddress(v: VoterFileContactDetail): string {
	const parts = [v.address_line1, v.city, v.state, v.zip].filter(Boolean);
	return parts.length > 0 ? parts.join(", ") : "\u2014";
}

function DetailField({
	label,
	value,
}: {
	label: string;
	value: string | number | boolean | null | undefined;
}) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="text-xs uppercase tracking-wide text-muted-foreground">
				{label}
			</span>
			<span className="text-sm text-foreground">{fmt(value)}</span>
		</div>
	);
}

function VoterFileContactDetailPage() {
	const { l2VoterId } = Route.useParams();
	const navigate = useNavigate();
	const session = useSession();
	const campaignId = session.campaign?.id;
	const { isReadWrite } = useRole();
	const queryClient = useQueryClient();

	const { data, isLoading, error } = useQuery({
		queryKey: ["voter-file-contact", campaignId, l2VoterId],
		queryFn: () => apiService.getVoterFileContactDetail(campaignId!, l2VoterId),
		enabled: !!campaignId,
	});

	const existingContactId = data?.existing_contact_id ?? null;
	useEffect(() => {
		if (existingContactId) {
			navigate({
				to: "/contacts/$contactId",
				params: { contactId: String(existingContactId) },
				replace: true,
			});
		}
	}, [existingContactId, navigate]);

	// PRD 41 §5.10 — emit voter_detail_viewed once the voter resolves.
	// No identifier captured (codex P1 — see analytics-events.ts):
	// l2_voter_id is a stable per-voter ID that would let PostHog
	// re-identify individual voters. Campaign / session context
	// already attached by PostHog identification is enough.
	//
	// The effect depends on l2VoterId so navigating between voter
	// details inside the same mounted route instance re-fires the
	// event for each voter (otherwise hasVoterRecord stays true and
	// the effect skips every voter after the first).
	const hasVoterRecord = Boolean(data && !existingContactId);
	useEffect(() => {
		if (hasVoterRecord) {
			trackVoterDetailViewed();
		}
	}, [hasVoterRecord, l2VoterId]);

	const importMutation = useMutation({
		mutationFn: () => apiService.importVoterFileContact(campaignId!, l2VoterId),
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: ["people", campaignId] });
			queryClient.invalidateQueries({ queryKey: ["contacts", campaignId] });
			if (result.status === "already_exists") {
				toast.info("This voter is already in your contacts — opening their record.");
			} else {
				toast.success("Voter added to your contacts.");
			}
			if (result.contact_id) {
				navigate({
					to: "/contacts/$contactId",
					params: { contactId: String(result.contact_id) },
				});
			}
		},
		onError: (err) => {
			toast.error(
				err instanceof Error ? err.message : "Failed to add voter to contacts.",
			);
		},
	});

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl">
					<p className="text-muted-foreground">
						No campaign found. Please complete onboarding first.
					</p>
				</div>
			</div>
		);
	}

	if (isLoading || existingContactId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl flex items-center justify-center py-20">
					<Spinner className="size-6" />
				</div>
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl space-y-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() =>
							navigate({ to: "/contacts", search: { tab: "your-voters" } })
						}
					>
						<X data-icon="inline-start" className="size-4" />
						Close
					</Button>
					<div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
						<p className="text-destructive">
							Could not load this voter. They may be outside your campaign's
							voter-file scope, or may no longer exist in the voter file.
						</p>
					</div>
				</div>
			</div>
		);
	}

	const displayName =
		[data.first_name, data.last_name].filter(Boolean).join(" ") || "\u2014";
	const scopeLabel = formatScope(data.locked_scope ?? {});

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-5xl space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between gap-4">
					<div className="space-y-2">
						<Button
							variant="ghost"
							size="sm"
							onClick={() =>
								navigate({ to: "/contacts", search: { tab: "your-voters" } })
							}
						>
							<X data-icon="inline-start" className="size-4" />
							Close
						</Button>
						<div className="flex items-center gap-3 flex-wrap">
							<h1 className="text-2xl font-semibold">{displayName}</h1>
							<Badge variant="secondary" className="gap-1">
								<Database className="size-3" />
								From Voter File
							</Badge>
							{scopeLabel && (
								<Badge variant="outline" className="text-muted-foreground">
									{scopeLabel}
								</Badge>
							)}
						</div>
						<p className="text-sm text-muted-foreground">
							L2 Voter ID: <span className="font-mono">{data.l2_voter_id}</span>
						</p>
					</div>

					{/* PRD 41 §5.5 — "Contact" verb menu replaces "Add to Contacts".
					    Each verb saves the voter to contacts if needed and then
					    navigates the user to the right surface to actually do
					    something. The verb is what the user is trying to do
					    ("text this voter"); the contact bookkeeping happens
					    behind it. */}
					<div className="shrink-0">
						{isReadWrite ? (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										disabled={importMutation.isPending}
										aria-label="Contact this voter"
									>
										{importMutation.isPending ? (
											<>
												<Spinner className="size-4" />
												Preparing…
											</>
										) : (
											<>
												<UserPlus data-icon="inline-start" className="size-4" />
												Contact
												<ChevronDown data-icon="inline-end" className="size-4" />
											</>
										)}
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" className="w-48">
									<DropdownMenuItem onSelect={() => importMutation.mutate()}>
										<MessageSquare className="size-4" />
										Text
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => importMutation.mutate()}>
										<Phone className="size-4" />
										Call
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => importMutation.mutate()}>
										<Mail className="size-4" />
										Email
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => importMutation.mutate()}>
										<PenLine className="size-4" />
										Add note
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => importMutation.mutate()}>
										<UsersRound className="size-4" />
										Add to group
									</DropdownMenuItem>
									<DropdownMenuItem
										onSelect={() => importMutation.mutate()}
										className="text-destructive focus:text-destructive"
									>
										<X className="size-4" />
										Mark do-not-contact
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						) : (
							<Tooltip>
								<TooltipTrigger asChild>
									<span>
										<Button disabled aria-label="Contact this voter">
											<UserPlus data-icon="inline-start" className="size-4" />
											Contact
										</Button>
									</span>
								</TooltipTrigger>
								<TooltipContent>
									Read-only users cannot perform this action
								</TooltipContent>
							</Tooltip>
						)}
					</div>
				</div>

				{/* Provenance banner */}
				<div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/30 dark:bg-amber-950/30 dark:text-amber-200">
					This voter lives in your campaign's voter file. Pick a Contact
					action above — we'll save them to your contacts and take you to
					the right place.
				</div>

				{/* Contact */}
				<Card>
					<CardHeader>
						<CardTitle>Contact</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<DetailField label="Email" value={data.email} />
						<DetailField label="Cell Phone" value={data.cell_phone} />
						<DetailField label="Landline" value={data.landline_phone} />
						<DetailField label="Do Not Call" value={data.do_not_call} />
						<DetailField label="Address" value={fullAddress(data)} />
					</CardContent>
				</Card>

				{/* Voter registration & districts */}
				<Card>
					<CardHeader>
						<CardTitle>Voter Registration</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
						<DetailField label="Party" value={data.party_affiliation} />
						<DetailField label="Registered" value={data.registration_date} />
						<DetailField label="Active Voter" value={data.voter_active} />
						<DetailField label="Precinct" value={data.precinct} />
						<DetailField
							label="Congressional District"
							value={
								data.congressional_district
									? `CD-${data.congressional_district}`
									: null
							}
						/>
						<DetailField
							label="State House"
							value={data.state_house_district}
						/>
						<DetailField
							label="State Senate"
							value={data.state_senate_district}
						/>
						<DetailField label="County" value={data.county} />
						<DetailField label="School District" value={data.school_district} />
					</CardContent>
				</Card>

				{/* Demographics */}
				<Card>
					<CardHeader>
						<CardTitle>Demographics</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
						<DetailField label="Age" value={data.age} />
						<DetailField label="Gender" value={data.gender} />
						<DetailField label="Ethnicity" value={data.ethnicity} />
						<DetailField label="Education" value={data.education} />
						<DetailField label="Language" value={data.language} />
						<DetailField label="Urbanicity" value={data.urbanicity} />
					</CardContent>
				</Card>

				{/* Voting behavior */}
				<Card>
					<CardHeader>
						<CardTitle>Voting Behavior</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
						<DetailField
							label="Vote Frequency"
							value={data.vote_frequency}
						/>
						<DetailField
							label="General Elections Voted"
							value={data.general_vote_total}
						/>
						<DetailField
							label="Primary Elections Voted"
							value={data.primary_vote_total}
						/>
						<DetailField
							label="Propensity (General)"
							value={data.propensity_general}
						/>
						<DetailField
							label="Propensity (Primary)"
							value={data.propensity_primary}
						/>
					</CardContent>
				</Card>

				{/* Modeled scores */}
				<Card>
					<CardHeader>
						<CardTitle>Modeled Scores</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
						<DetailField
							label="Progressive Dem"
							value={data.progressive_dem_score}
						/>
						<DetailField
							label="Moderate Dem"
							value={data.moderate_dem_score}
						/>
						<DetailField
							label="Moderate Rep"
							value={data.moderate_rep_score}
						/>
						<DetailField
							label="Conservative Rep"
							value={data.conservative_rep_score}
						/>
						<DetailField
							label="Small Dollar Donor"
							value={data.small_dollar_donor_score}
						/>
						<DetailField label="Activist" value={data.activist_model_score} />
					</CardContent>
				</Card>

				{/* FEC donor */}
				<Card>
					<CardHeader>
						<CardTitle>FEC Donor History</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
						<DetailField label="Avg Donation" value={data.fec_avg_donation} />
						<DetailField
							label="Total Donations"
							value={data.fec_total_donations}
						/>
						<DetailField
							label="Donation Count"
							value={data.fec_donation_count}
						/>
					</CardContent>
				</Card>

				{/* Flags + consumer */}
				<Card>
					<CardHeader>
						<CardTitle>Other</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
						<DetailField label="Veteran" value={data.veteran} />
						<DetailField label="Likely Union" value={data.likely_union} />
						<DetailField label="Business Owner" value={data.business_owner} />
						<DetailField label="Changed Party" value={data.changed_party} />
						<DetailField label="Political Donor" value={data.political_donor} />
						<DetailField label="Homeowner" value={data.homeowner} />
						<DetailField label="Dwelling" value={data.dwelling_type} />
						<DetailField
							label="Household Income"
							value={data.household_income}
						/>
						<DetailField
							label="Credit Range"
							value={data.financial_credit_range}
						/>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
